import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import villageUrl from '../assets/village.png';
import './VillageIllustration.css';

interface VillageIllustrationProps {
  /** 'recto' is the desktop two-page-spread size; 'mobile' is the collapsed single-page size. */
  size?: 'recto' | 'mobile';
}

/**
 * The Susdle frontispiece illustration: a hand-printed woodcut of the village
 * sitting on the recto under the title plate. Asset is a public-domain
 * 19th-century engraving (827×455 PNG, dark linework on transparent) housed
 * in this component's assets folder.
 *
 * The plate is bracketed top and bottom by short ash-soft hairlines so the
 * image reads as set into the page rather than floating on cream.
 * Decorative — `role='img'` on the figure carries the accessible name; the
 * raster image itself is aria-hidden.
 */
export function VillageIllustration({ size = 'recto' }: VillageIllustrationProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <figure
      className="susdle-village"
      data-size={size}
      role="img"
      aria-label={t('lobby.village.ariaLabel')}
    >
      <img
        className="susdle-village__plate"
        src={villageUrl}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
      />
    </figure>
  );
}
